import { ConfigurationCard } from "features/settings/common/styles";
import Title from "common/Typography/Title";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import React, { useMemo, useState } from "react";
import { useTheme } from "styled-components";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import { GroupData, LanguageNameSpaces } from "@shared/types";
import CancelGroupSubscriptionModal from "features/configuration/GroupConfigurationDrawer/CancelGroupSubscriptionModal";
import GroupSubscription from "features/settings/Billing/GroupSubscription";
import moment from "moment";
import { getLifetimeDate } from "@shared/utils/date";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookGroupSubscriptions from "features/settings/Billing/NewLookGroupSubscriptions.tsx";

const GroupSubscriptions = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const appTheme = useTheme();
  const { data, isFetching, isLoading } =
    sharedLib.sharedApi.settings.useGetGroupsDataQuery();
  const [subscriptionGroup, setSubscriptionGroup] = useState<GroupData>();

  const sortedData = useMemo(() => {
    const now = moment().unix();
    const getExpiration = (groupData: GroupData) => {
      const expiration =
        groupData.next_auto_renewal ||
        groupData.expiration_timestamp ||
        getLifetimeDate().unix();
      return Math.abs(now - expiration);
    };

    return data?.slice().sort((a, b) => {
      return (
        +(b.support_payments || false) - +(a.support_payments || false) ||
        getExpiration(a) - getExpiration(b)
      );
    });
  }, [data]);

  const now = moment().unix();
  const activeGroups: GroupData[] = [];
  const expiredGroups: GroupData[] = [];
  sortedData?.forEach((groupData) => {
    const isActive =
      (groupData.expiration_timestamp &&
        groupData.expiration_timestamp > now) ||
      groupData.next_auto_renewal;
    if (isActive) {
      activeGroups.push(groupData);
    } else {
      expiredGroups.push(groupData);
    }
  });

  const cancelModal = (
    <CancelGroupSubscriptionModal
      closeModal={() => setSubscriptionGroup(undefined)}
      groupData={subscriptionGroup}
      visible={!!subscriptionGroup}
    />
  );

  if (appTheme.isNewLook) {
    return (
      <>
        <NewLookGroupSubscriptions
          activeGroups={activeGroups}
          expiredGroups={expiredGroups}
          isLoading={isLoading}
          isFetching={isFetching}
          onCancelAutoRenewal={setSubscriptionGroup}
        />
        {cancelModal}
      </>
    );
  }

  const getExpiredGroups = () => {
    if (expiredGroups.length) {
      return (
        <>
          <Title level={3} style={{ margin: "28px 0 16px 0" }}>
            {t("group_subscriptions.expired_title")}
          </Title>
          <EqualColumnsGrid numColumns={3}>
            {expiredGroups.map((groupData) => (
              <GroupSubscription
                key={groupData.id}
                groupData={groupData}
                onCancelAutoRenewal={() => setSubscriptionGroup(groupData)}
              />
            ))}
          </EqualColumnsGrid>
        </>
      );
    }
  };

  return (
    <ConfigurationCard style={{ marginTop: 16 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        {t("group_subscriptions.title")}
      </Title>
      <LoadingSpinnerOverlay
        isLoading={isFetching}
        backgroundColor={appTheme.colors.cardBgColor}
        hideContentWhileLoading={isLoading}
      >
        <EqualColumnsGrid numColumns={3}>
          {activeGroups.map((groupData) => (
            <GroupSubscription
              key={groupData.id}
              groupData={groupData}
              onCancelAutoRenewal={() => setSubscriptionGroup(groupData)}
            />
          ))}
        </EqualColumnsGrid>
        {getExpiredGroups()}
      </LoadingSpinnerOverlay>
      {cancelModal}
    </ConfigurationCard>
  );
};

export default GroupSubscriptions;
